import { ChevronDown, Zap, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TRIGGER_MAP, ACTION_MAP } from "./constants";

/**
 * FlowTree — vertical branching visualisation for an automation.
 * Structure:
 *   [Trigger node]        ← top
 *        │
 *   [Condition badge?]    ← if any
 *        │
 *   [Action 1]            ← boxes, connected by lines
 *        │
 *   [Delay ×]
 *        │
 *   [Action 2]
 *
 * Not fully editable inline (the modal uses forms for that) but it renders as
 * the user picks trigger/actions so they see the shape of what they're building.
 */
export function FlowTree({ triggerType, conditions = [], actions = [] }) {
  const { t } = useTranslation();
  const T = TRIGGER_MAP[triggerType] || TRIGGER_MAP.manual;
  const TIcon = T.icon;

  return (
    <div className="rounded-2xl bg-gradient-to-b from-[color:var(--sp-brand)]/5 to-transparent border border-dashed border-[color:var(--sp-brand)]/30 p-6" data-testid="flow-tree">
      <div className="flex flex-col items-center gap-1">
        {/* Trigger */}
        <FlowNode
          tone="brand"
          icon={<TIcon className="h-4 w-4" />}
          eyebrow="Quando"
          title={t(T.label)}
        />

        {/* Condition badge */}
        {conditions.length > 0 && (
          <>
            <FlowLine />
            <FlowBadge>
              {conditions.length === 1 ? "Solo se 1 condizione è vera" : `Solo se ${conditions.length} condizioni sono tutte vere`}
            </FlowBadge>
          </>
        )}

        {/* Actions */}
        {actions.length === 0 ? (
          <>
            <FlowLine />
            <FlowNode tone="ghost" icon={<Zap className="h-4 w-4" />} eyebrow="Poi" title="Nessuna azione ancora" />
          </>
        ) : actions.map((a, i) => {
          const A = ACTION_MAP[a.type] || ACTION_MAP.log;
          const AIcon = A.icon;
          const isWait = a.type === "wait";
          const dv = a.delay_value ?? 0;
          const du = a.delay_unit || "minutes";
          return (
            <ActionStep key={i} a={a} A={A} AIcon={AIcon} isWait={isWait} dv={dv} du={du} />
          );
        })}
      </div>
    </div>
  );
}

function ActionStep({ a, A, AIcon, isWait, dv, du }) {
  const { t } = useTranslation();
  return (
    <>
      <FlowLine />
      <FlowNode
        tone={isWait ? "slate" : "cyan"}
        icon={<AIcon className="h-4 w-4" />}
        eyebrow={isWait ? "Aspetta" : "Poi esegui"}
        title={isWait ? `${dv} ${humanUnit(du, dv)}` : t(A.label)}
        subtitle={a.type === "add_tag" ? a.config?.tag_name : a.type === "send_whatsapp" ? a.config?.template_name : a.type === "add_note" ? a.config?.note : null}
      />
    </>
  );
}

function humanUnit(u, n) {
  if (u === "seconds") return n === 1 ? "secondo" : "secondi";
  if (u === "minutes") return n === 1 ? "minuto" : "minuti";
  if (u === "hours") return n === 1 ? "ora" : "ore";
  if (u === "days") return n === 1 ? "giorno" : "giorni";
  return u;
}

function FlowNode({ tone = "brand", icon, eyebrow, title, subtitle }) {
  const tones = {
    brand: "bg-white border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]",
    cyan: "bg-white border-[color:var(--sp-border)]",
    slate: "bg-slate-50 border-slate-200 text-slate-600",
    ghost: "bg-transparent border-dashed border-[color:var(--sp-border-strong)] text-[color:var(--sp-text-muted)]",
  };
  const iconWrap = {
    brand: "bg-[color:var(--sp-brand)] text-white",
    cyan: "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]",
    slate: "bg-slate-200 text-slate-600",
    ghost: "bg-slate-100 text-slate-400",
  };
  return (
    <div className={`min-w-[240px] max-w-sm rounded-2xl border-2 px-4 py-3 shadow-sm ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        <span className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconWrap[tone]}`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{eyebrow}</div>
          <div className="font-bold text-sm text-[color:var(--sp-text)] truncate">{title}</div>
          {subtitle && <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate italic">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function FlowLine() {
  return (
    <div className="flex flex-col items-center py-1">
      <div className="w-0.5 h-4 bg-[color:var(--sp-brand)]/40" />
      <ChevronDown className="h-3 w-3 text-[color:var(--sp-brand)]/50 -mt-1" />
    </div>
  );
}

function FlowBadge({ children }) {
  return (
    <div className="px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-bold text-amber-800 inline-flex items-center gap-1">
      🔎 {children}
    </div>
  );
}
